import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { uploadImage } from '../services/cloudinary';
import heic2any from 'heic2any';
import Masonry from 'react-masonry-css';
import MaterialIcon from '../shared/ui/icons/MaterialIcon';
import '../styles/WeddingPhotoGallery.css';

const WeddingPhotoGallery = ({
  title,
  description,
  layout,
  columns,
  photos = [],
  allowUpload = true,
  uploadButtonText,
  onPhotoUpload,
  theme,
  sectionId
}) => {
  const { websiteId } = useParams();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadError, setUploadError] = useState('');

  const MAX_UPLOADS = 10;
  const STORAGE_KEY_PHOTOS = `wedding_Uploads_photos_${websiteId}_${sectionId}`;
  const STORAGE_KEY_COUNT = `wedding_Uploads_count_${websiteId}_${sectionId}`;

  useEffect(() => {
    const storedCount = localStorage.getItem(STORAGE_KEY_COUNT);
    if (storedCount) {
      setUploadCount(parseInt(storedCount, 10));
    }
  }, [STORAGE_KEY_COUNT]);

  useEffect(() => {
    if (theme) {
      const root = document.documentElement;
      if (theme.colors) {
        root.style.setProperty('--theme-primary-color', theme.colors.primary);
        root.style.setProperty('--theme-text-color', theme.colors.text);
        root.style.setProperty('--theme-background-color', theme.colors.background);
        root.style.setProperty('--theme-secondary-color', theme.colors.secondary);
        root.style.setProperty('--theme-accent-color', theme.colors.accent);
      }
      if (theme.spacing) {
        root.style.setProperty('--theme-spacing-unit', theme.spacing.unit);
        if (theme.spacing.scale) {
          theme.spacing.scale.forEach((value, index) => {
            root.style.setProperty(`--theme-spacing-scale-${index}`, value);
          });
        }
      }
      if (theme.borderRadius) {
        root.style.setProperty('--theme-border-radius-small', theme.borderRadius.small);
        root.style.setProperty('--theme-border-radius-medium', theme.borderRadius.medium);
        root.style.setProperty('--theme-border-radius-large', theme.borderRadius.large);
      }
      if (theme.shadows) {
        root.style.setProperty('--theme-shadow-small', theme.shadows.small);
        root.style.setProperty('--theme-shadow-medium', theme.shadows.medium);
        root.style.setProperty('--theme-shadow-large', theme.shadows.large);
      }
      if (theme.fonts) {
        root.style.setProperty('--theme-font-heading', theme.fonts.heading);
        root.style.setProperty('--theme-font-body', theme.fonts.body);
      }
    }
  }, [theme]);

  const storeUploadedPhoto = (uploadResponse) => {
    const photoUrl = uploadResponse.secure_url;
    if (!photoUrl) {
      console.error('No secure_url in uploadResponse:', uploadResponse);
      return;
    }
    const storedPhotos = localStorage.getItem(STORAGE_KEY_PHOTOS);
    let userPhotos = [];
    try {
      userPhotos = storedPhotos ? JSON.parse(storedPhotos) : [];
      if (!Array.isArray(userPhotos)) {
        console.warn('Stored photos is not an array, resetting:', userPhotos);
        userPhotos = [];
      }
    } catch (error) {
      console.error('Error parsing stored photos:', error);
      userPhotos = [];
    }
    if (!userPhotos.includes(photoUrl)) {
      userPhotos.push(photoUrl);
      try {
        localStorage.setItem(STORAGE_KEY_PHOTOS, JSON.stringify(userPhotos));
        console.log('Successfully stored photo in localStorage:', { photoUrl, userPhotos });
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    } else {
      console.log('Photo already exists in localStorage:', photoUrl);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadError('');
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setUploadError(`Файлът е твърде голям. Максималният размер е 10MB. Вашият файл е ${(file.size / (1024 * 1024)).toFixed(1)}MB.`);
      return;
    }

    let uploadFile = file;
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
    if (isHeic) {
      try {
        console.log('Converting HEIC file:', file.name);
        const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 });
        uploadFile = new File([convertedBlob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
        console.log('Converted to JPEG:', uploadFile.name, uploadFile.size, uploadFile.type);
      } catch (error) {
        console.error('Error converting HEIC file:', error);
        setUploadError('Грешка при конвертиране на HEIC снимка. Моля, изберете друг формат (JPG, PNG, GIF).');
        return;
      }
    } else if (!file.type.startsWith('image/')) {
      setUploadError('Моля, изберете файл със снимка (JPG, PNG, GIF, HEIC).');
      return;
    }

    if (uploadCount >= MAX_UPLOADS) {
      setUploadError(`Достигнат е лимитът от ${MAX_UPLOADS} снимки за това устройство.`);
      return;
    }

    try {
      setIsUploading(true);
      const uploadResponse = await uploadImage(uploadFile);
      console.log('Upload response in handlePhotoUpload:', uploadResponse);
      storeUploadedPhoto(uploadResponse);
      const newCount = uploadCount + 1;
      setUploadCount(newCount);
      localStorage.setItem(STORAGE_KEY_COUNT, newCount.toString());
      if (onPhotoUpload) {
        onPhotoUpload(uploadResponse.secure_url);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      let errorMessage = 'Грешка при качването на снимката. Моля, опитайте отново.';
      if (error.message.includes('413') || error.message.includes('Request Entity Too Large')) {
        errorMessage = 'Файлът е твърде голям за сървъра. Моля, изберете по-малка снимка (под 10MB).';
      } else if (error.message.includes('CORS') || error.message.includes('Access-Control')) {
        errorMessage = 'Проблем с връзката към сървъра. Моля, свържете се с администратора.';
      } else if (error.message.includes('Invalid file type')) {
        errorMessage = 'Неподдържан тип файл. Моля, изберете снимка (JPG, PNG, GIF, HEIC).';
      }
      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const breakpointColumns = {
    default: columns,
    1100: Math.min(columns, 3),
    700: Math.min(columns, 2),
    500: 1
  };

  const remainingUploads = MAX_UPLOADS - uploadCount;
  const canUpload = remainingUploads > 0 && allowUpload;

  return (
    <div className="wedding-photo-gallery">
      <div className="gallery-header">
        <h2 className="gallery-title">
          {title}
          <div className="title-underline" />
        </h2>
        <p className="gallery-description">
          {description}
        </p>
        {allowUpload && (
          <div className="upload-counter upload-counter-spaced">
            <span className={`counter-text ${remainingUploads <= 3 ? 'warning' : ''}`}>
              {remainingUploads} от {MAX_UPLOADS} снимки остават
            </span>
          </div>
        )}
      </div>

      {allowUpload && (
        <div className="upload-section">
          <label className={`upload-button ${isUploading ? 'loading' : ''} ${!canUpload ? 'disabled' : ''}`}>
            <MaterialIcon name="upload" size={20} className="upload-icon" />
            {isUploading ? 'Качване...' : (canUpload ? uploadButtonText : 'Лимит достигнат')}
            <input
              type="file"
              accept="image/*,image/heic,image/heif"
              onChange={handlePhotoUpload}
              className="upload-input-hidden"
              disabled={isUploading || !canUpload}
            />
          </label>
          {uploadError && (
            <div className="upload-error">
              <MaterialIcon name="error" size={16} />
              {uploadError}
            </div>
          )}
        </div>
      )}

      {photos.length > 0 && (
        <Masonry
          breakpointCols={breakpointColumns}
          className="masonry-grid"
          columnClassName="masonry-grid-column"
        >
          {photos.map((photo, index) => (
            <div key={index} className="photo-item">
              <img
                src={photo}
                alt={`Сватбен кадър ${index + 1}`}
                className="photo-image"
                onError={() => console.error('Failed to load image:', photo)}
              />
            </div>
          ))}
        </Masonry>
      )}

      <div className="view-gallery-section">
        <Link
          to={`/site/${websiteId}/gallery/${sectionId}`}
          className="view-gallery-button"
        >
          Вижте снимките
        </Link>
      </div>
    </div>
  );
};

export default WeddingPhotoGallery;
